import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { ArrowRight, Briefcase, Check, Heart, Home, LifeBuoy, Plane, Users } from 'lucide-react';
import { Container, Glow, Section, SectionHeader } from './ui';
import { Reveal, Stagger, fadeUp } from './motion';

const AUDIENCES = [
  {
    icon: Briefcase,
    title: 'Joint Ventures',
    description: 'Collaborating on a business idea or project with shared financial resources.',
    tag: 'Perfect for startups and business partners',
    accent: 'text-blue-300 bg-blue-400/10 border-blue-400/20',
    span: 'lg:col-span-2',
  },
  {
    icon: Home,
    title: 'Roommates',
    description: 'Splitting rent, utilities, or shared expenses without the hassle of tracking who owes what.',
    tag: 'Manage household expenses effortlessly',
    accent: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/20',
  },
  {
    icon: Plane,
    title: 'Friends',
    description: 'Planning a trip or a big group gift with complete transparency and trust.',
    tag: 'Travel and group gifts made easy',
    accent: 'text-amber-300 bg-amber-400/10 border-amber-400/20',
  },
  {
    icon: Heart,
    title: 'Families',
    description: 'Supporting each other or saving for something special as a family unit.',
    tag: 'Save for family goals together',
    accent: 'text-pink-300 bg-pink-400/10 border-pink-400/20',
  },
  {
    icon: Users,
    title: 'Communities',
    description: 'Pooling resources for a shared cause or community project with full accountability.',
    tag: 'Fund community initiatives transparently',
    accent: 'text-violet-300 bg-violet-400/10 border-violet-400/20',
  },
  {
    icon: LifeBuoy,
    title: 'Emergency Funds',
    description: 'Helping out in tough times with a transparent and accessible emergency response fund.',
    tag: 'Quick mobilization when it matters most',
    accent: 'text-rose-300 bg-rose-400/10 border-rose-400/20',
    span: 'lg:col-span-2',
  },
];

const Audience = () => (
  <Section id="audience" className="overflow-hidden">
    <Glow className="left-1/2 top-0 h-80 w-[48rem] -translate-x-1/2 bg-blue-600/10" />
    <Container className="relative">
      <SectionHeader
        eyebrow="For Everyone"
        title="Who Is Groupmoney For?"
        description="Our platform works for all kinds of people and situations."
      />

      <Stagger className="mt-16 grid gap-4 sm:mt-20 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5" stagger={0.07}>
        {AUDIENCES.map(({ icon: Icon, title, description, tag, accent, span }) => (
          <motion.article
            key={title}
            variants={fadeUp}
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
            className={clsx('surface flex flex-col p-7 transition-colors duration-300 hover:border-white/[0.16]', span)}
          >
            <span className={clsx('flex h-12 w-12 items-center justify-center rounded-2xl border', accent)}>
              <Icon className="h-6 w-6" />
            </span>
            <h3 className="mt-6 text-xl font-bold tracking-tight text-white">{title}</h3>
            <p className="mt-2 flex-1 leading-relaxed text-slate-400">{description}</p>
            <p className="mt-6 flex items-center gap-2 border-t border-white/[0.06] pt-5 text-sm text-slate-300">
              <Check className="h-4 w-4 text-brand-300" />
              {tag}
            </p>
          </motion.article>
        ))}
      </Stagger>

      <Reveal className="mx-auto mt-20 max-w-3xl text-center">
        <p className="text-xl leading-relaxed text-slate-300 sm:text-2xl sm:leading-relaxed">
          No matter who you are or what you're planning, Groupmoney provides the tools and transparency to make group finances simple and stress-free.
        </p>
        <a href="#how-it-works" className="btn-primary group mt-10">
          Find Your Solution
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        </a>
      </Reveal>
    </Container>
  </Section>
);

export default Audience;
